import React from 'react';
import { motion } from 'framer-motion';
export interface MrAvatarProps {
  size?: number;
  expression?: 'happy' | 'confident' | 'thinking' | 'winking' | 'surprised';
  accessory?: 'none' | 'santa-hat' | 'pumpkin-face' | 'sunglasses' | 'party-hat';
  hairStyle?: 'eyebrows' | 'hairline';
  animate?: boolean;
  className?: string;
}
export function MrAvatar({
  size = 200,
  expression = 'happy',
  accessory = 'none',
  hairStyle = 'eyebrows',
  animate = true,
  className = ''
}: MrAvatarProps) {
  // Base Y offset if the M-wave is acting as a hairline instead of eyebrows
  const waveOffsetY = hairStyle === 'hairline' ? -25 : 0;
  // --- EXPRESSION PATHS ---
  // M-Wave (Eyebrows/Hairline)
  const getWavePath = () => {
    switch (expression) {
      case 'confident':
        // Left side droops down, right side is sharp
        return `M 45 ${100 + waveOffsetY} Q 65 ${45 + waveOffsetY} 100 ${85 + waveOffsetY} Q 125 ${35 + waveOffsetY} 155 ${85 + waveOffsetY}`;
      case 'thinking':
        // Furrowed, slightly squished
        return `M 55 ${85 + waveOffsetY} Q 75 ${65 + waveOffsetY} 100 ${85 + waveOffsetY} Q 125 ${65 + waveOffsetY} 145 ${85 + waveOffsetY}`;
      case 'surprised':
        // Raised high
        return `M 50 ${70 + waveOffsetY} Q 75 ${20 + waveOffsetY} 100 ${70 + waveOffsetY} Q 125 ${20 + waveOffsetY} 150 ${70 + waveOffsetY}`;
      case 'happy':
      case 'winking':
      default:
        // Standard symmetrical M
        return `M 50 ${85 + waveOffsetY} Q 75 ${35 + waveOffsetY} 100 ${85 + waveOffsetY} Q 125 ${35 + waveOffsetY} 150 ${85 + waveOffsetY}`;
    }
  };
  // Mouth
  const getMouth = () => {
    switch (expression) {
      case 'confident':
        // Smirk (asymmetrical)
        return <path d="M 70 140 Q 100 160 135 130" stroke="#0785B7" strokeWidth="12" strokeLinecap="round" fill="none" data-id="element-422" data-mp="2wocyr5botj4r" />;
      case 'thinking':
        // Small 'o'
        return <circle cx="100" cy="145" r="8" fill="#0785B7" data-id="element-423" data-mp="28wxifl0hjf2l" />;
      case 'surprised':
        // Big 'O'
        return <circle cx="100" cy="145" r="14" fill="none" stroke="#0785B7" strokeWidth="8" data-id="element-424" data-mp="23xasqc3h06cw" />;
      case 'happy':
      case 'winking':
      default:
        // Wide smile
        return <path d="M 65 135 Q 100 170 135 135" stroke="#0785B7" strokeWidth="14" strokeLinecap="round" fill="none" data-id="element-425" data-mp="1p96hft6qyx45" />;
    }
  };
  // Eyes
  const getEyes = () => {
    const eyeProps = {
      fill: '#ffffff'
    };
    switch (expression) {
      case 'thinking':
        // Looking up-left
        return <>
            <circle cx="65" cy="100" r="10" {...eyeProps} data-id="element-426" data-mp="16igfc6p03ecc" />
            <circle cx="125" cy="100" r="10" {...eyeProps} data-id="element-427" data-mp="16gvk0euv3rdf" />
          </>;
      case 'surprised':
        // Big eyes
        return <>
            <circle cx="70" cy="105" r="14" {...eyeProps} data-id="element-428" data-mp="3d65y121o6072" />
            <circle cx="130" cy="105" r="14" {...eyeProps} data-id="element-429" data-mp="3hxxh6rihx3n6" />
          </>;
      case 'winking':
        // Left open, right winking curve
        return <>
            <circle cx="70" cy="105" r="10" {...eyeProps} data-id="element-430" data-mp="344fyf8xmvuy7" />
            <path d="M 120 105 Q 130 95 140 105" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" fill="none" data-id="element-431" data-mp="396os67ibz11d" />
          </>;
      case 'confident':
      case 'happy':
      default:
        // Standard eyes
        return <>
            <circle cx="70" cy="105" r="10" {...eyeProps} data-id="element-432" data-mp="3mz4n0l12vsr7" />
            <circle cx="130" cy="105" r="10" {...eyeProps} data-id="element-433" data-mp="3rv2792fgpj99" />
          </>;
    }
  };
  // --- ACCESSORIES ---
  const renderAccessory = () => {
    switch (accessory) {
      case 'santa-hat':
        return <g transform="translate(40, -10)" data-id="element-434" data-mp="2gij5hf7pv1yw">
            <path d="M 20 50 L 100 50 L 60 0 Z" fill="#ef4444" data-id="element-435" data-mp="2vqcvvouyh4dy" />
            <rect x="15" y="45" width="90" height="15" rx="7.5" fill="#ffffff" data-id="element-436" data-mp="2vrwf9obwrvjy" />
            <circle cx="60" cy="0" r="12" fill="#ffffff" data-id="element-437" data-mp="2mz2uxchlu5z8" />
          </g>;
      case 'party-hat':
        return <g transform="translate(60, -10)" data-id="element-438" data-mp="2hx3p7vye6wz4">
            <path d="M 10 50 L 70 50 L 40 0 Z" fill="#8b5cf6" data-id="element-439" data-mp="3epz688o0o2ke" />
            <circle cx="40" cy="0" r="8" fill="#14b8a6" data-id="element-440" data-mp="3en4nqmokcrtm" />
            <path d="M 20 50 L 30 20 M 40 50 L 50 20 M 60 50 L 60 30" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" opacity="0.5" data-id="element-441" data-mp="3ap6wzff59xo4" />
          </g>;
      case 'sunglasses':
        return <g transform="translate(0, 95)" data-id="element-442" data-mp="1uanh2ta7ltph">
            <rect x="50" y="0" width="40" height="25" rx="8" fill="#0f172a" data-id="element-443" data-mp="1u7j69d28nzpu" />
            <rect x="110" y="0" width="40" height="25" rx="8" fill="#0f172a" data-id="element-444" data-mp="1p8y59bf5xc0c" />
            <path d="M 90 10 L 110 10" stroke="#0f172a" strokeWidth="4" data-id="element-445" data-mp="1f7ndbtyksqqo" />
            <path d="M 30 5 L 50 10 M 170 5 L 150 10" stroke="#0f172a" strokeWidth="4" data-id="element-446" data-mp="1k5ffdgsxpt8l" />
            {/* Glare */}
            <path d="M 55 5 L 75 5 M 115 5 L 135 5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.3" data-id="element-447" data-mp="1p2p7533sqgzl" />
          </g>;
      case 'pumpkin-face':
        // Overlays a pumpkin texture/lines on the face
        return <g opacity="0.15" data-id="element-448" data-mp="27vjbz47pgsq3">
            <path d="M 100 20 Q 80 100 100 180" stroke="#000000" strokeWidth="4" fill="none" data-id="element-449" data-mp="27wb2w1qcy2a1" />
            <path d="M 100 20 Q 50 100 100 180" stroke="#000000" strokeWidth="4" fill="none" data-id="element-450" data-mp="dhia0aguzpls" />
            <path d="M 100 20 Q 150 100 100 180" stroke="#000000" strokeWidth="4" fill="none" data-id="element-451" data-mp="iglhe1kba7wx" />
          </g>;
      case 'none':
      default:
        return null;
    }
  };
  const svgContent = <svg width={size} height={size} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-452" data-mp="keuvqhr1nvab">
      <defs data-id="element-453" data-mp="te8anpxxtc2f">
        {/* Face Gradient — matches hero-gradient (navy-purple) */}
        <linearGradient id={`faceGradient-${size}`} x1="0" y1="0" x2="200" y2="200" gradientUnits="userSpaceOnUse" data-id="element-454" data-mp="ofeufpr9n6yk">
          <stop offset="0%" stopColor="#0F1D3A" data-id="element-455" data-mp="136dx6k5f71a8" />
          <stop offset="50%" stopColor="#1B1B4B" data-id="element-456" data-mp="y4yh76i3nkyp" />
          <stop offset="100%" stopColor="#2D1B69" data-id="element-457" data-mp="y7kkuv1vdgbn" />
        </linearGradient>

        {/* M-Wave Gradient — white to brand teal */}
        <linearGradient id={`waveGradient-${size}`} x1="40" y1="0" x2="160" y2="0" gradientUnits="userSpaceOnUse" data-id="element-458" data-mp="183xoolr4pak4">
          <stop offset="0%" stopColor="#ffffff" data-id="element-459" data-mp="1vqszq3xiqdn1" />
          <stop offset="60%" stopColor="#0785B7" data-id="element-460" data-mp="1vphqholb0doj" />
          <stop offset="100%" stopColor="#0785B7" data-id="element-461" data-mp="1hra0y1qlkene" />
        </linearGradient>

        {/* Background Blob Gradient — brand teal */}
        <linearGradient id={`blobGradient-${size}`} x1="0" y1="0" x2="200" y2="200" gradientUnits="userSpaceOnUse" data-id="element-462" data-mp="1mp1sgunbxyr2">
          <stop offset="0%" stopColor="#0785B7" stopOpacity="0.5" data-id="element-463" data-mp="1cs6uqdyvs2du" />
          <stop offset="100%" stopColor="#0785B7" stopOpacity="0.15" data-id="element-464" data-mp="1ctrmxwxq398j" />
        </linearGradient>

        {/* Inner shadow for face depth */}
        <radialGradient id={`faceInnerShadow-${size}`} cx="50%" cy="50%" r="50%" data-id="element-465" data-mp="1335gas7zht1z">
          <stop offset="70%" stopColor="transparent" data-id="element-466" data-mp="p34enajwahlg" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.3" data-id="element-467" data-mp="u1yz0gxige7o" />
        </radialGradient>
      </defs>

      {/* Background Organic Blob */}
      <path d="M 170 60 C 190 90 180 140 150 170 C 120 200 70 190 40 160 C 10 130 20 80 50 50 C 80 20 150 30 170 60 Z" fill={`url(#blobGradient-${size})`} data-id="element-468" data-mp="19inq07pwvme9" />

      {/* Main Face Base */}
      <rect x="25" y="25" width="150" height="150" rx="40" fill={`url(#faceGradient-${size})`} data-id="element-469" data-mp="10kv8qy0mq4o8" />
      {/* Inner shadow overlay for depth */}
      <rect x="25" y="25" width="150" height="150" rx="40" fill={`url(#faceInnerShadow-${size})`} data-id="element-470" data-mp="qmnvap5ju4qz" />

      {/* Pumpkin Texture Overlay (if active) */}
      {accessory === 'pumpkin-face' && renderAccessory()}

      {/* M-Wave (Eyebrows/Hairline) */}
      <path d={getWavePath()} stroke={`url(#waveGradient-${size})`} strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" fill="none" style={{
      transition: 'all 0.3s ease-in-out'
    }} data-id="element-471" data-mp="cr3fi2l4a2ns" />

      {/* Eyes */}
      <g style={{
      transition: 'all 0.3s ease-in-out'
    }} data-id="element-472" data-mp="2ekga2ald6p0f">
        {getEyes()}
      </g>

      {/* Mouth */}
      <g style={{
      transition: 'all 0.3s ease-in-out'
    }} data-id="element-473" data-mp="2og056us3tjo4">
        {getMouth()}
      </g>

      {/* Other Accessories (Hats, Glasses) */}
      {accessory !== 'pumpkin-face' && renderAccessory()}
    </svg>;
  if (animate) {
    return <motion.div animate={{
      y: [0, -8, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="inline-block" data-id="element-474" data-mp="1vq1c8oash2e6">
        {svgContent}
      </motion.div>;
  }
  return <div className="inline-block" data-id="element-475" data-mp="29ldfjk30g16p">{svgContent}</div>;
}